/**
 * RoutingRulesEditor — the rule rows of a pure-orchestrator template (used inside the template form).
 * Template example: demo `/form-elements` (inputs in a row) with `ItemList`-style dividers.
 */
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { Input } from '../../components/tailgrids/core/input.js';
import { TextField } from '../../components/tailgrids/core/text-field.js';
import { Button } from '../../components/ui/index.js';

/** One routing rule for a pure-orchestrator template (mirrors RoutingRuleConfig). */
export interface RoutingRule {
  match: string;
  targetTemplate: string;
  context?: string;
}

export interface RoutingRulesEditorProps {
  rules: RoutingRule[];
  onChange: (rules: RoutingRule[]) => void;
}

const INPUT_CLASS = 'w-full px-3 py-2 text-sm';

/**
 * Presentational editor for a pure-orchestrator's routing rules. Rules are
 * evaluated in order, first case-insensitive substring match wins. The parent
 * owns state; this component only emits new arrays via onChange.
 */
export const RoutingRulesEditor: React.FC<RoutingRulesEditorProps> = ({ rules, onChange }) => {
  const update = (i: number, patch: Partial<RoutingRule>) =>
    onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rules.filter((_, idx) => idx !== i));
  const add = () => onChange([...rules, { match: '', targetTemplate: '' }]);

  return (
    <div className="flex flex-col gap-3">
      {rules.length === 0 ? (
        <p className="text-sm text-muted">
          No routing rules yet. Add at least one — a pure orchestrator with no rules has nothing to route to.
        </p>
      ) : (
        <ol className="flex flex-col">
          {rules.map((rule, i) => (
            <li
              key={`rule-${i}`}
              className="flex items-start gap-3 border-b border-border-primary py-3 first:pt-0 last:border-0"
            >
              <span className="w-5 pt-2.5 text-right text-xs text-muted">{i + 1}</span>
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
                <TextField
                  value={rule.match}
                  onChange={(match) => update(i, { match })}
                  aria-label={`Rule ${i + 1} match`}
                >
                  <Input placeholder="If input contains…" className={INPUT_CLASS} />
                </TextField>
                <div className="flex items-center gap-1">
                  <ArrowRight size={14} className="shrink-0 text-faint" aria-hidden="true" />
                  <TextField
                    value={rule.targetTemplate}
                    onChange={(targetTemplate) => update(i, { targetTemplate })}
                    aria-label={`Rule ${i + 1} target template`}
                    className="w-full"
                  >
                    <Input placeholder="target-template-slug" className={INPUT_CLASS} />
                  </TextField>
                </div>
                <TextField
                  value={rule.context ?? ''}
                  onChange={(context) => update(i, { context: context || undefined })}
                  aria-label={`Rule ${i + 1} context`}
                >
                  <Input placeholder="Prepended context (optional)" className={INPUT_CLASS} />
                </TextField>
              </div>
              <Button variant="ghost" size="sm" iconOnly aria-label={`Remove rule ${i + 1}`} onClick={() => remove(i)}>
                <Trash2 size={15} aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ol>
      )}
      <div>
        <Button variant="secondary" size="sm" onClick={add}>
          <Plus size={14} aria-hidden="true" />
          Add rule
        </Button>
      </div>
    </div>
  );
};
